import {useMemo, useState} from 'react'
import {IconMapPin, IconPlus, IconX} from '@tabler/icons-react'
import {createPlace} from './api/places'
import {pathMatches, placePath, searchWords} from './api/tree'
import type {TreeIndex} from './api/types'

interface Props {
  ix: TreeIndex
  value: string | null
  onChange: (handle: string | null) => void
  /** новое место заведено — древо надо перечитать, чтобы оно появилось в справочнике */
  onCreated?: (handle: string, title: string) => void
  placeholder?: string
}

/** Выбор места из справочника с возможностью завести новое прямо здесь.
 *  Место создаётся сразу, а не при сохранении: справочник общий, и лишняя
 *  запись в нём — не потеря, её видно и легко убрать в разделе «Места». */
export default function PlacePicker({ix, value, onChange, onCreated, placeholder = 'место не указано'}: Props) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState<Map<string, string>>(new Map())

  // полное название собирается по иерархии: «ул. Садовая, Мерефа, Украина».
  // Короткого имени в выборе мало — «Мерефа» и «Мерефа» из разных стран
  // не различить.
  const all = useMemo(() => {
    const m = new Map<string, string>()
    for (const h of ix.places.keys()) {
      const t = placePath(ix, h)
      if (t) m.set(h, t)
    }
    fresh.forEach((t, h) => m.set(h, t))
    return m
  }, [ix, fresh])

  const hits = useMemo(() => {
    const v = q.trim().toLowerCase()
    const list = [...all.entries()].filter(([, t]) => t)
    if (!v) return list.slice(0, 8)
    return list.filter(([, t]) => pathMatches(t, searchWords(q))).slice(0, 8)
  }, [all, q])

  const exact = [...all.values()].some(t => t.trim().toLowerCase() === q.trim().toLowerCase())
  const chosen = value ? all.get(value) : null

  async function add() {
    const t = q.trim()
    if (!t || busy) return
    setBusy(true)
    try {
      const h = await createPlace(t)
      setFresh(m => new Map(m).set(h, t))
      onChange(h)
      onCreated?.(h, t)
      setQ('')
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  if (chosen) {
    return (
      <div className="picked">
        <IconMapPin size={15} stroke={1.7} className="pmark" />
        <span className="pn">{chosen}</span>
        <button type="button" className="icobtn" title="Убрать" onClick={() => onChange(null)}>
          <IconX size={15} stroke={1.8} />
        </button>
      </div>
    )
  }

  return (
    <div className="picker">
      <input
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 170)}
        placeholder={placeholder}
      />
      {open && (
        <div className="sresults">
          {hits.map(([h, t]) => (
            <div key={h} onMouseDown={() => { onChange(h); setQ(''); setOpen(false) }}>{t}</div>
          ))}
          {q.trim() && !exact && (
            <div className="mk" onMouseDown={add}>
              <IconPlus size={13} stroke={2} /> Завести место «{q.trim()}»
            </div>
          )}
          {!hits.length && !q.trim() && <div className="muted">Справочник пуст</div>}
        </div>
      )}
    </div>
  )
}
